import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowLeft, Store, Globe, Lock, CreditCard } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming"
];

const COUNTRIES = [
  { code: "US", name: "United States", region: "North America" },
  { code: "CA", name: "Canada", region: "North America" },
  { code: "GB", name: "United Kingdom", region: "Europe" },
  { code: "JM", name: "Jamaica", region: "Caribbean" },
  { code: "TT", name: "Trinidad and Tobago", region: "Caribbean" },
  { code: "BB", name: "Barbados", region: "Caribbean" },
  { code: "BS", name: "Bahamas", region: "Caribbean" },
  { code: "NG", name: "Nigeria", region: "Africa" },
  { code: "GH", name: "Ghana", region: "Africa" },
  { code: "KE", name: "Kenya", region: "Africa" },
  { code: "ZA", name: "South Africa", region: "Africa" },
  { code: "BR", name: "Brazil", region: "South America" },
  { code: "FR", name: "France", region: "Europe" },
  { code: "DE", name: "Germany", region: "Europe" },
  { code: "NL", name: "Netherlands", region: "Europe" },
  { code: "AU", name: "Australia", region: "Oceania" },
  { code: "MX", name: "Mexico", region: "North America" },
  { code: "PR", name: "Puerto Rico", region: "Caribbean" },
  { code: "CR", name: "Costa Rica", region: "Central America" },
  { code: "PA", name: "Panama", region: "Central America" },
];

const CATEGORIES = [
  { id: 'Restaurants', name: 'Restaurants' },
  { id: 'Retail', name: 'Retail' },
  { id: 'Services', name: 'Services' },
  { id: 'Professional', name: 'Professional' },
  { id: 'Beauty & Wellness', name: 'Beauty & Wellness' },
  { id: 'Churches', name: 'Churches' },
  { id: 'Non-Profits', name: 'Non-Profits' },
  { id: 'Foundations', name: 'Foundations' },
  { id: 'Charities', name: 'Charities' },
  { id: 'Organizations', name: 'Organizations' },
  { id: 'Photography', name: 'Photography' },
  { id: 'Videography', name: 'Videography' },
  { id: 'Graphic Design', name: 'Graphic Design' },
  { id: 'Museums', name: 'Museums' },
  { id: 'Film Production', name: 'Film Production' },
  { id: 'Music Recording Company', name: 'Music Recording Company' },
  { id: 'Adventures', name: 'Adventures' },
  { id: 'Cruises', name: 'Cruises' },
  { id: 'Vacation Rentals', name: 'Vacation Rentals' },
  { id: 'Travel Agencies', name: 'Travel Agencies' },
];

const AddBusinessPage = () => {
  const navigate = useNavigate();
  const { token, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    country: 'United States',
    phone: '',
    email: '',
    website: '',
    image_url: '',
  });

  const isUSA = formData.country === 'United States';

  // Check if user has an active owner subscription
  useEffect(() => {
    const checkSubscription = async () => {
      if (!isAuthenticated) {
        setCheckingSubscription(false);
        return;
      }

      try {
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const response = await axios.get(`${API}/my-subscription`, {
          headers,
          withCredentials: true
        });
        
        const sub = response.data;
        setSubscription(sub);
        
        // Check if user has any owner subscription (basic, pro, or premium)
        if (sub.owner_subscription && ['owner_basic', 'owner_pro', 'owner_premium'].includes(sub.owner_subscription)) {
          setHasSubscription(true);
        } else {
          setHasSubscription(false);
        }
      } catch (error) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Failed to check subscription:', error);
        }
        setHasSubscription(false);
      } finally {
        setCheckingSubscription(false);
      }
    };

    checkSubscription();
  }, [isAuthenticated, token]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => {
      const newData = { ...prev, [name]: value };
      // Clear state and zip if changing to non-US country
      if (name === 'country' && value !== 'United States') {
        newData.state = '';
        newData.zip_code = '';
      }
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation - state and zip only required for US
    if (!formData.name || !formData.description || !formData.category ||
        !formData.address || !formData.city || !formData.country) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // For US, require state and zip
    if (isUSA && (!formData.state || !formData.zip_code)) {
      toast.error('Please fill in state and ZIP code for US businesses');
      return;
    }

    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(`${API}/businesses`, formData, {
        headers,
        withCredentials: true
      });
      toast.success('Business created successfully!');
      navigate(`/business/${response.data.business_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create business');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-white" data-testid="add-business-page">
      <Navbar />

      <div className="pt-24 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 text-stone-500 hover:text-emerald-deep mb-4 transition-colors"
              data-testid="back-dashboard-link"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Link>
            <h1 className="font-display text-3xl font-bold text-stone-900" data-testid="page-title">
              Add Your Business
            </h1>
            <p className="text-stone-600 mt-1">
              List your Black-owned business and reach more customers
            </p>
          </div>

          {/* Loading State */}
          {checkingSubscription && (
            <div className="bg-white rounded-xl p-12 border border-stone-200 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-emerald-deep border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-stone-600">Checking your subscription...</p>
            </div>
          )}

          {/* Paywall - No Subscription */}
          {!checkingSubscription && !hasSubscription && (
            <div className="bg-white rounded-xl p-8 border border-stone-200 text-center">
              <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lock className="w-8 h-8 text-stone-400" />
              </div>
              <h2 className="font-display text-2xl font-bold text-stone-900 mb-3">
                Subscription Required
              </h2>
              <p className="text-stone-600 mb-6 max-w-md mx-auto">
                To list your business on Uprize Black Biz, you'll need an active business listing subscription. 
                Choose a plan that fits your needs.
              </p>
              
              {/* Plan Options */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 max-w-2xl mx-auto">
                <div className="p-4 border border-stone-200 rounded-lg text-left">
                  <h3 className="font-semibold text-stone-900">Basic</h3>
                  <p className="text-2xl font-bold text-emerald-deep">$19.99<span className="text-sm font-normal text-stone-500">/mo</span></p>
                  <p className="text-sm text-stone-500">1 business listing</p>
                </div>
                <div className="p-4 border-2 border-emerald-deep rounded-lg text-left bg-emerald-50">
                  <h3 className="font-semibold text-stone-900">Pro <span className="text-xs bg-emerald-deep text-white px-2 py-0.5 rounded-full">Popular</span></h3>
                  <p className="text-2xl font-bold text-emerald-deep">$49.99<span className="text-sm font-normal text-stone-500">/mo</span></p>
                  <p className="text-sm text-stone-500">Up to 5 listings</p>
                </div>
                <div className="p-4 border border-stone-200 rounded-lg text-left">
                  <h3 className="font-semibold text-stone-900">Premium</h3>
                  <p className="text-2xl font-bold text-emerald-deep">$99.99<span className="text-sm font-normal text-stone-500">/mo</span></p>
                  <p className="text-sm text-stone-500">Unlimited listings</p>
                </div>
              </div>

              <Button
                onClick={() => navigate('/pricing')}
                className="bg-emerald-deep hover:bg-emerald-deep/90 text-white rounded-full px-8 h-12"
                data-testid="subscribe-btn"
              >
                <CreditCard className="w-5 h-5 mr-2" />
                Choose a Plan to Continue
              </Button>
              
              <p className="text-sm text-stone-500 mt-4">
                Already subscribed? <Link to="/login" className="text-emerald-deep hover:underline">Sign in</Link> to access your account.
              </p>
            </div>
          )}

          {/* Form - Only show if has subscription */}
          {!checkingSubscription && hasSubscription && (
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Disclaimer */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
              <div className="flex gap-3">
                <div className="flex-shrink-0">
                  <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-amber-800 mb-1">Important: Black-Owned Businesses Only</h3>
                  <p className="text-sm text-amber-700">
                    Uprize Black Biz is dedicated exclusively to supporting and promoting <strong>Black-owned businesses</strong>. 
                    By submitting your business, you confirm that it is a Black-owned establishment that is legally registered 
                    with your state and maintains active status.
                  </p>
                  <p className="text-sm text-amber-700 mt-2">
                    <strong>Requirements:</strong> All businesses must be legally registered. Food trucks and restaurants must 
                    have current, valid permits and all required licenses. Businesses serving alcohol must maintain proper, 
                    up-to-date alcohol permits and licenses. We prioritize customer satisfaction and safety.
                  </p>
                  <p className="text-sm text-amber-700 mt-2">
                    <strong>Policy:</strong> If your business is found to not be Black-owned, not legally registered, or lacking 
                    proper permits/licenses, your listing will be deactivated and all purchases will be refunded. You may 
                    re-register once all legal requirements are properly met.
                  </p>
                </div>
              </div>
            </div>

            {/* Basic Info */}
            <div className="bg-white rounded-xl p-6 border border-stone-200">
              <h2 className="font-display text-xl font-semibold text-stone-900 mb-6 flex items-center gap-2">
                <Store className="w-5 h-5 text-emerald-deep" />
                Basic Information
              </h2>

              <div className="space-y-5">
                <div>
                  <Label htmlFor="name" className="text-stone-700">
                    Business Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g., Soul Food Kitchen"
                    className="mt-1"
                    data-testid="business-name-input"
                  />
                </div>

                <div>
                  <Label htmlFor="description" className="text-stone-700">
                    Description <span className="text-red-500">*</span>
                  </Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    placeholder="Tell customers about your business..."
                    rows={4}
                    className="mt-1"
                    data-testid="business-description-input"
                  />
                </div>

                <div>
                  <Label htmlFor="category" className="text-stone-700">
                    Category <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) => handleSelectChange('category', value)}
                  >
                    <SelectTrigger className="mt-1" data-testid="category-select">
                      <SelectValue placeholder="Select a category" />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="image_url" className="text-stone-700">
                    Image URL
                  </Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      id="image_url"
                      name="image_url"
                      value={formData.image_url}
                      onChange={handleChange}
                      placeholder="https://example.com/image.jpg"
                      data-testid="image-url-input"
                    />
                  </div>
                  <p className="text-sm text-stone-500 mt-1">
                    Enter a URL to an image of your business (optional)
                  </p>
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="bg-white rounded-xl p-6 border border-stone-200">
              <h2 className="font-display text-xl font-semibold text-stone-900 mb-6 flex items-center gap-2">
                <Globe className="w-5 h-5 text-emerald-deep" />
                Location
              </h2>

              <div className="space-y-5">
                <div>
                  <Label htmlFor="country" className="text-stone-700">
                    Country <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.country}
                    onValueChange={(value) => handleSelectChange('country', value)}
                  >
                    <SelectTrigger className="mt-1" data-testid="country-select">
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {COUNTRIES.map((country) => (
                        <SelectItem key={country.code} value={country.name}>
                          {country.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="address" className="text-stone-700">
                    Street Address <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    placeholder="123 Main Street"
                    className="mt-1"
                    data-testid="address-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="city" className="text-stone-700">
                      City <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="city"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      placeholder={isUSA ? "Atlanta" : "City"}
                      className="mt-1"
                      data-testid="city-input"
                    />
                  </div>
                  {isUSA ? (
                    <div>
                      <Label htmlFor="state" className="text-stone-700">
                        State <span className="text-red-500">*</span>
                      </Label>
                      <Select
                        value={formData.state}
                        onValueChange={(value) => handleSelectChange('state', value)}
                      >
                        <SelectTrigger className="mt-1" data-testid="state-select">
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                        <SelectContent>
                          {US_STATES.map((state) => (
                            <SelectItem key={state} value={state}>
                              {state}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ) : (
                    <div>
                      <Label htmlFor="state" className="text-stone-700">
                        State/Province/Region
                      </Label>
                      <Input
                        id="state"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                        placeholder="Region (optional)"
                        className="mt-1"
                        data-testid="state-input"
                      />
                    </div>
                  )}
                </div>

                {isUSA ? (
                  <div className="w-1/2">
                    <Label htmlFor="zip_code" className="text-stone-700">
                      ZIP Code <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="zip_code"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleChange}
                      placeholder="30301"
                      className="mt-1"
                      data-testid="zip-input"
                    />
                  </div>
                ) : (
                  <div className="w-1/2">
                    <Label htmlFor="zip_code" className="text-stone-700">
                      Postal Code
                    </Label>
                    <Input
                      id="zip_code"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleChange}
                      placeholder="Postal code (optional)"
                      className="mt-1"
                      data-testid="zip-input"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Contact */}
            <div className="bg-white rounded-xl p-6 border border-stone-200">
              <h2 className="font-display text-xl font-semibold text-stone-900 mb-6">
                Contact Information
              </h2>

              <div className="space-y-5">
                <div>
                  <Label htmlFor="phone" className="text-stone-700">Phone Number</Label>
                  <Input
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="(404) 555-0123"
                    className="mt-1"
                    data-testid="phone-input"
                  />
                </div>

                <div>
                  <Label htmlFor="email" className="text-stone-700">Business Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="contact@yourbusiness.com"
                    className="mt-1"
                    data-testid="email-input"
                  />
                </div>

                <div>
                  <Label htmlFor="website" className="text-stone-700">Website</Label>
                  <Input
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://yourbusiness.com"
                    className="mt-1"
                    data-testid="website-input"
                  />
                </div>
              </div>
            </div>

            {/* Submit */}
            <div className="flex gap-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="flex-1 h-12 rounded-full"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 h-12 bg-emerald-deep hover:bg-emerald-deep/90 text-white rounded-full"
                data-testid="submit-business-btn"
              >
                {loading ? 'Creating...' : 'Create Business'}
              </Button>
            </div>
          </form>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AddBusinessPage;
